'use strict';

import React, { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useCart, useCartDispatch } from "../../../common/CartContext";
import { subscribe, publish } from "../../../common/EventDispatcher";
import { prepareCheckout, formatCurrency } from "../../../common/CartHelpers";
import "./ShoppingCart.scss";
import CartItem from "./CartItem";
import CardList from "./CardList";

const backdrop = {
  visible: {
    opacity: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.3,
    },
  },
  hidden: {
    opacity: 0,
    transition: {
      when: "afterChildren",
    },
  },
  exit: {
    opacity: 0,
    transition: {
      when: "afterChildren",
    },
  },
};

const sidebar = {
  visible: { opacity: 1, x: 0 },
  hidden: { opacity: 0, x: 50 },
  exit: { opacity: 0, x: 50 },
};

export default function ShoppingCart() {
  const [show, setShow] = useState(false);
  const [inCheckout, setInCheckout] = useState(false);
  const [isPayingInvoice, setIsPayingInvoice] = useState(false);
  const cartContents = useCart();
  const dispatch = useCartDispatch();

  /**
   * Prevent cart from opening if paying an invoice
   */
  useEffect(() => {
    subscribe('isPayingInvoice', () => setIsPayingInvoice(true));
  }, []);

  const cartTotal = useMemo(() => {
    return cartContents.reduce((total, item) => total + item.quantity * item.unitPrice, 0);
  }, [cartContents]);

  const cartItemCount = useMemo(() => {
    return cartContents.reduce((sum, item) => sum + item.quantity, 0);
  }, [cartContents]);

  const handleCheckoutClick = () => {
    prepareCheckout();
  };

  const handleCartOpen = () => {
    if (!isPayingInvoice) {
      setShow(true);
    }
  };

  useEffect(() => {
    const elements = Array.from(document.getElementsByClassName("navbar-cart"));
    elements.forEach((el) => el.addEventListener("click", handleCartOpen));

    subscribe('productAddedToCart', handleCartOpen);
    subscribe('openCart', handleCartOpen);

    return () => {
      elements.forEach((el) => el.removeEventListener("click", handleCartOpen));
    };
  }, [isPayingInvoice]);

  // 🔔 Update cart count badge
  useEffect(() => {
    const cartElements = document.getElementsByClassName("navbar-cart");

    Array.from(cartElements).forEach((el) => {
      if (isPayingInvoice) {
        el.classList.add("invoice-disabled");
      }

      let badge = el.querySelector(".cart-count");
      if (cartItemCount > 0) {
        if (!badge) {
          badge = document.createElement("span");
          badge.className = "cart-count";
          el.appendChild(badge);
        }
        badge.textContent = cartItemCount.toString();
      } else {
        if (badge) {
          badge.remove();
        }
      }
    });

    if (window.location.pathname.indexOf('/checkout') !== -1) {
      setInCheckout(true);
    }
  }, [cartItemCount, isPayingInvoice]);

  const handleClose = () => {
    setShow(false);
    publish('cartClosed');
  };

  const handleBackdropClick = (e) => {
    if (e.target.id === "sidebar-backdrop") {
      setShow(false);
      publish('cartClosed');
    }
  };

  const handleIncrement = (item) => {
    dispatch({
      type: "UPDATE_CART",
      nsProductId: item.nsProductId,
      nid: item.nid,
      name: item.name,
      icon: item.icon,
      unitPrice: item.unitPrice,
      quantity: item.quantity + 1
    });
  };

  const handleDecrement = (item) => {
    if (item.quantity === 1) {
      dispatch({
        type: "REMOVE_FROM_CART",
        nid: item.nid,
      });
    } else {
      dispatch({
        type: "UPDATE_CART",
        nsProductId: item.nsProductId,
        nid: item.nid,
        name: item.name,
        icon: item.icon,
        unitPrice: item.unitPrice,
        quantity: item.quantity - 1
      });
    }
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          id="sidebar-backdrop"
          className="sidebar-backdrop"
          onClick={handleBackdropClick}
          key="sidebar-backdrop"
          variants={backdrop}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <motion.div
            className="sidebar"
            key="sidebar"
            variants={sidebar}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <div className="shopping-cart__header">
              <h1>Your Cart</h1>
              <button className="shopping-card__close-button" onClick={handleClose}>
                <i className="fa-solid fa-circle-xmark"></i>
              </button>
            </div>
            {cartContents.length > 0 ? (
              cartContents.map((item) => (
                <CartItem
                  key={item.nid}
                  item={item}
                  handleDecrement={handleDecrement}
                  handleIncrement={handleIncrement}
                />
              ))
            ) : (
              <p>Your cart is empty</p>
            )}

            {cartTotal > 0 && (
              <div className="cart-total">
                <div className="cart-total__spacer"></div>
                <span className="cart-total__title d-flex flex-grow-1">Total</span>
                <span className="cart-total__number">${formatCurrency(cartTotal)}</span>
              </div>
            )}

            {cartContents.length !== 0 && (
            <div className="cart-tax-disclaimer">
              <span>Taxes will be calculated after order is placed. <br/></span>
              <span>Purchases can only be made with a credit card.</span>
            </div>
            )}

            {cartContents.length !== 0 && (
              <div className="checkout-button">
                <button className="primary-button" onClick={handleCheckoutClick}>{inCheckout ? "Update Cart" : "Checkout"}</button>
                {!inCheckout && (
                  <button className="secondary-button mt-3" onClick={handleClose}>Continue Shopping</button>
                )}

              </div>
            )}
            {cartContents.length > 0 && (
              <CardList />
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
